import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

interface SolairePageProps {
  t: any;
  isRTL: boolean;
}

const SolairePageContainer = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 0;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled.div<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const BreadcrumbLink = styled.button<{ $isRTL: boolean }>`
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: #b91c1c;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #94a3b8;
  margin: 0 0.5rem;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled(motion.h1)<{ $isRTL: boolean }>`
  font-size: 3.5rem;
  color: #1f2937;
  margin-bottom: 1.5rem;
  font-weight: 700;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Separator = styled.div`
  width: 100px;
  height: 4px;
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  margin: 0 auto 2rem;
  border-radius: 2px;
`;

const ContentSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const SolaireImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const TextContent = styled.div<{ $isRTL: boolean }>`
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const ContentTitle = styled.h2<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  color: #1f2937;
  margin-bottom: 2rem;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ContentText = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #4b5563;
  margin-bottom: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const HighlightText = styled.span`
  color: #dc2626;
  font-weight: 600;
`;

const PDFSection = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  margin-top: 3rem;
`;

const PDFSectionTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 2rem;
  color: #1f2937;
  margin-bottom: 2rem;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const PDFCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  max-width: 400px;
  margin: 0 auto;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const PDFIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #dc2626, #ef4444);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: white;
  margin: 0 auto 1.5rem;
`;

const PDFCardTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  color: #1f2937;
  margin-bottom: 1rem;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const SolairePage: React.FC<SolairePageProps> = ({ t, isRTL }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handlePDFDownload = (pdfName: string) => {
    const pdfPath = `/images/solaire/${pdfName}`;
    const link = document.createElement('a');
    link.href = pdfPath;
    link.download = pdfName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBreadcrumbClick = () => {
    navigate('/');
  };

  return (
    <SolairePageContainer $isRTL={isRTL}>
      <Container>
        <Breadcrumbs $isRTL={isRTL}>
          <BreadcrumbLink $isRTL={isRTL} onClick={handleBreadcrumbClick}>
            {t.home}
          </BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <span>{t.solairePageBreadcrumbServices}</span>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <span>{t.solairePageBreadcrumbSolaire}</span>
        </Breadcrumbs>

        <HeaderSection>
          <PageTitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t.solairePageTitle}
          </PageTitle>
          <Separator />
        </HeaderSection>

        <ContentSection>
          <ImageContainer>
            <SolaireImage 
              src="/images/solaire/solaire.jpg" 
              alt="Solutions Solaires" 
            />
          </ImageContainer>
          
          <TextContent $isRTL={isRTL}>
            <ContentTitle $isRTL={isRTL}>
              {t.solairePageContentTitle}
            </ContentTitle>
            
            <ContentText $isRTL={isRTL}>
              {t.solairePageContent1}
            </ContentText>
            
            <ContentText $isRTL={isRTL}>
              {t.solairePageContent2}
            </ContentText>
            
            <ContentText $isRTL={isRTL}>
              {t.solairePageContent3}
            </ContentText>
            
            <ContentText $isRTL={isRTL}>
              <HighlightText>{t.techyid}</HighlightText> {t.solairePageContent4}
            </ContentText>
          </TextContent>
        </ContentSection>

        <PDFSection>
          <PDFSectionTitle $isRTL={isRTL}>
            {t.solairePageDocumentationTitle}
          </PDFSectionTitle>
          
          <PDFCard
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handlePDFDownload('Catalogue offre solaire (2).pdf')}
          >
            <PDFIcon>📄</PDFIcon>
            <PDFCardTitle $isRTL={isRTL}>
              {t.solairePageCatalogueTitle}
            </PDFCardTitle>
          </PDFCard>
        </PDFSection>
      </Container>
      <Footer t={t} isRTL={isRTL} />
    </SolairePageContainer>
  );
};

export default SolairePage; 